import { Button, InputField, Select, Theme } from "@in/component-library";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { DEFAULT_COUNTRY_CODE } from "../../../utils/constants";
import { ApiResponse } from "../../apis/api-error";
import { setForeignAccount } from "../../apis/set-foreign-account";

import countries from "../../../utils/countries";
import { useLayout } from "../../hooks/use-layout";

interface CreateForeignAccountProps {
  onClose: () => void;
  selectAccount: (accountId: string, isExternal: boolean) => void;
  setAccountInfo: (organizationNumber: string, address: string, name: string) => void;
  isSaving: boolean;
}

type FormData = {
  [key: string]: {
    value: string;
    error?: string;
    touched?: boolean;
    errorMessage?: string;
  };
};

const CreateForeignAccount: FC<CreateForeignAccountProps> = ({ onClose, selectAccount, setAccountInfo, isSaving }) => {
  const { translations } = useLayout();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [needValidation, setNeedValidation] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    accountName: {
      value: "",
      error: "",
      touched: false,
      errorMessage: translations.foreignAccountCompanyNameError
    },
    phoneNumber: {
      value: "",
      error: ""
    },
    street: {
      value: "",
      error: "",
      touched: false,
      errorMessage: translations.foreignAccountCompanyAddressError
    },
    city: {
      value: "",
      error: "",
      touched: false,
      errorMessage: translations.foreignAccountCityError
    },
    postalCode: {
      value: "",
      error: "",
      touched: false,
      errorMessage: translations.foreignAccountPostalCodeError
    },
    countryName: {
      value: "",
      error: "",
      touched: false
    },
    countryCode: {
      value: "",
      error: "",
      touched: false
    },
    countryCodePhone: {
      value: DEFAULT_COUNTRY_CODE
    }
  });

  const toAddAccount = async () => {
    const response = await setForeignAccount({
      accountName: formData.accountName.value,
      phoneNumber: `${formData.countryCodePhone.value} ${formData.phoneNumber.value}`,
      street: formData.street.value,
      city: formData.city.value,
      postalCode: formData.city.value,
      countryName: formData.countryName.value,
      countryCode: formData.countryCode.value
    });

    const handleOk = (response: ApiResponse) => {
      const accountID: string = response.body;
      selectAccount(accountID.toString(), true);
      setAccountInfo("", `${formData.postalCode.value} ${formData.city.value}`, formData.accountName.value);
    };

    const handleBadRequest = (response: ApiResponse) => {
      const error: {
        errorType: string;
        invalidFields: string[];
      } = response.body;
      setFormData({
        ...formData,
        postalCode: {
          ...formData.postalCode,
          error: error.invalidFields.some((f) => f === "PostalCode") ? "Invalid postalcode" : formData.postalCode.error
        }
      });
    };

    const handleError = () => {
      setApiError(translations.internalServerError);
    };

    switch (response.code) {
      case 200:
        handleOk(response);
        break;
      case 400:
        handleBadRequest(response);
        break;
      case 500:
        handleError();
        break;
      default:
        handleError();
        break;
    }
  };

  const updateFormField = (fieldName: string, fields: { value: string; touched?: boolean }) => {
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        ...fields
      }
    }));
    setNeedValidation(true);
  };

  const chooseCountryName = (country: string) => {
    updateFormField("countryName", { value: country, touched: true });

    const findCountryCode = countries.find((c) => c.name === country)?.isoCode as string;
    updateFormField("countryCode", { value: findCountryCode, touched: true });
  };

  useEffect(() => {
    if (needValidation) {
      let formIsValid = true;

      const updatetFormData: FormData = { ...formData };

      for (const fieldName in formData) {
        if (
          formData[fieldName].touched === true &&
          formData[fieldName].value === "" &&
          formData[fieldName].error === ""
        ) {
          updatetFormData[fieldName].error = formData[fieldName].errorMessage;
        }

        if (
          formData[fieldName].touched === true &&
          formData[fieldName].value !== "" &&
          formData[fieldName].error !== ""
        ) {
          updatetFormData[fieldName].error = "";
        }
      }

      for (const formField in formData) {
        if (formData[formField].value === "" && "touched" in formData[formField]) {
          formIsValid = false;
          break;
        }
      }

      setFormData(updatetFormData);
      setDisabled(!formIsValid);
      setNeedValidation(false);
    }
  }, [formData, needValidation]);

  return (
    <>
      <p className="foreign-account__text">{translations.foreignAccountText}</p>

      <div className="foreign-account__form">
        <InputField
          aria-live="assertive"
          className="data-hj-suppress foreign-account__input"
          name="accountName"
          label={translations.foreignAccountCompanyName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            updateFormField("accountName", {
              value: (e.target as HTMLInputElement).value,
              touched: true
            });
          }}
          value={formData.accountName.value}
          errorMsg={formData.accountName.error}
        />
        <InputField
          aria-live="assertive"
          className="data-hj-suppress foreign-account__input"
          name="street"
          label={translations.foreignAccountCompanyAddress}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            updateFormField("street", {
              value: (e.target as HTMLInputElement).value,
              touched: true
            });
          }}
          value={formData.street.value}
          errorMsg={formData.street.error}
        />
        <InputField
          aria-live="assertive"
          className="data-hj-suppress foreign-account__input"
          name="city"
          label={translations.foreignAccountCity}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            updateFormField("city", {
              value: (e.target as HTMLInputElement).value,
              touched: true
            });
          }}
          value={formData.city.value}
          errorMsg={formData.city.error}
        />
        <InputField
          aria-live="assertive"
          className="data-hj-suppress foreign-account__input"
          name="postalCode"
          label={translations.foreignAccountPostalCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            updateFormField("postalCode", {
              value: (e.target as HTMLInputElement).value,
              touched: true
            });
          }}
          value={formData.postalCode.value}
          errorMsg={formData.postalCode.error}
        />

        <Select
          name="countryName"
          className="data-hj-suppress foreign-account__input"
          label={translations.foreignAccountCompanyCountryName}
          value={formData.countryName.value}
          onChange={(country: string) => chooseCountryName(country)}
          options={countries
            .filter((c) => c.isoCode !== "NO")
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((e) => ({
              value: e.name,
              text: e.name
            }))}
        />

        <div className="foreign-account__phone">
          <Select
            className="data-hj-suppress foreign-account__input"
            name="countryCode"
            label={translations.chooseAccountCountryCodeLabel}
            placeholder={translations.countryCodeLabel}
            value={formData.countryCodePhone.value}
            onChange={(code: string) =>
              updateFormField("countryCodePhone", {
                value: code || DEFAULT_COUNTRY_CODE,
                touched: true
              })
            }
            options={[
              {
                dialCode: DEFAULT_COUNTRY_CODE,
                isoCode: "NO"
              },
              ...countries.sort((a, b) => a.isoCode.localeCompare(b.isoCode))
            ].map((e) => ({
              value: e.dialCode,
              text: `${e.isoCode} ${e.dialCode}`
            }))}
          />
          <InputField
            aria-live="assertive"
            type="tel"
            className="data-hj-suppress foreign-account__input"
            name="phoneNumber"
            label={translations.foreignAccountCompanyPhone}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              updateFormField("phoneNumber", {
                value: (e.target as HTMLInputElement).value,
                touched: true
              });
            }}
            value={formData.phoneNumber.value}
            errorMsg={formData.phoneNumber.error}
          />
        </div>
      </div>

      {apiError && <div className="foreign-account__api-error">{apiError}</div>}

      <div aria-live="polite" className="foreign-account__actions">
        <Button
          variant="solid"
          theme={Theme.Neutral}
          disabled={isSaving || disabled}
          isLoading={isSaving}
          onClick={toAddAccount}
        >
          {translations.chooseAccountNextLabel}
        </Button>
        <Button variant="outlined" theme={Theme.Neutral} onClick={onClose}>
          {translations.personalOrExternalAccountsCancelButton}
        </Button>
      </div>
    </>
  );
};

export default CreateForeignAccount;
